import React, {useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";
import {
    Table,
    Card,
    Typography,
    Upload,
    Button,
    Modal,
    Form,
    Select,
    DatePicker,
    TimePicker,
    Input,
    message,
    Row,
    Col,
    Divider
} from "antd";
import {
    CheckCircleTwoTone,
    CloseCircleTwoTone,
    PlusOutlined,
    SearchOutlined,
    UploadOutlined
} from "@ant-design/icons";
import api from "../../api";
import dayjs from "dayjs";

const {Title} = Typography;
const {Option} = Select;
const {RangePicker} = DatePicker;

const TransportationPage: React.FC = () => {
    const [transportations, setTransportations] = useState<any[]>([]);
    const [customers, setCustomers] = useState<any[]>([]);
    const [modalOpen, setModalOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [form] = Form.useForm();
    const [searchText, setSearchText] = useState("");
    const [arrivalDateRange, setArrivalDateRange] = useState<any>(null);
    const [departureDateRange, setDepartureDateRange] = useState<any>(null);
    const [arrivalLocation, setArrivalLocation] = useState<string | undefined>(undefined);
    const [departureLocation, setDepartureLocation] = useState<string | undefined>(undefined);
    const arrivalTransportation = Form.useWatch("arrival_transportation", form);
    const departureTransportation = Form.useWatch("departure_transportation", form);
    const [total, setTotal] = useState(0)
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
    });
    const navigate = useNavigate();

    useEffect(() => {
        fetchTransportations();
        fetchCustomers();
    }, [searchText, arrivalDateRange, departureDateRange, arrivalLocation, departureLocation]);

    useEffect(() => {
        fetchTransportations();
        fetchCustomers();
    }, []);

    const fetchTransportations = async (page = 1, pageSize = 10) => {
        setLoading(true);
        try {
            const params: any = {
                page,
                page_size: pageSize,
            };

            if (searchText) params.search = searchText;
            if (arrivalLocation) params.arrival_location = arrivalLocation;
            if (departureLocation) params.departure_location = departureLocation;

            if (arrivalDateRange?.[0] && arrivalDateRange?.[1]) {
                params.arrival_date_after = arrivalDateRange[0].format("YYYY-MM-DD");
                params.arrival_date_before = arrivalDateRange[1].format("YYYY-MM-DD");
            }

            if (departureDateRange?.[0] && departureDateRange?.[1]) {
                params.departure_date_after = departureDateRange[0].format("YYYY-MM-DD");
                params.departure_date_before = departureDateRange[1].format("YYYY-MM-DD");
            }

            const res = await api.get("/cc/transportation/", {params});

            const results = res.data.results || res.data;
            setTotal(res.data.count || results.length);

            const today = dayjs();

            const sorted = [...results].sort((a: any, b: any) => {
                const aDate = dayjs(a.arrival_date || a.departure_date);
                const bDate = dayjs(b.arrival_date || b.departure_date);

                const aIsPast =
                    (a.arrival_date && a.departure_date && dayjs(a.departure_date).isBefore(today, "day")) ||
                    (a.arrival_date && !a.departure_date && aDate.isBefore(today, "day")) ||
                    (!a.arrival_date && a.departure_date && aDate.isBefore(today, "day"));

                const bIsPast =
                    (b.arrival_date && b.departure_date && dayjs(b.departure_date).isBefore(today, "day")) ||
                    (b.arrival_date && !b.departure_date && bDate.isBefore(today, "day")) ||
                    (!b.arrival_date && b.departure_date && bDate.isBefore(today, "day"));

                if (aIsPast !== bIsPast) return aIsPast ? 1 : -1;

                return bDate.valueOf() - aDate.valueOf();
            });

            setTransportations(sorted);
        } catch {
            message.error("Failed to load transportation data");
        } finally {
            setLoading(false);
        }
    };

    const fetchCustomers = async () => {
        try {
            const res = await api.get("/cc/customers/");
            setCustomers(res.data.results);
        } catch {
            message.error("Failed to load customers");
        }
    };

    const autofillFromFlights = async () => {
        const customerId = form.getFieldValue("customer");
        const arrivalDate = form.getFieldValue("arrival_date");
        const departureDate = form.getFieldValue("departure_date");

        if (!customerId || (!arrivalDate && !departureDate)) {
            return;
        }

        try {
            const res = await api.get(`/cc/flights/?customer=${customerId}`);
            const flights = res.data.results;

            const arrivalFlight = flights.find(
                (f: any) =>
                    f.in_arrival_date === arrivalDate?.format("YYYY-MM-DD")
            );

            const departureFlight = flights.find(
                (f: any) =>
                    f.out_departure_date === departureDate?.format("YYYY-MM-DD")
            );

            if (arrivalFlight) {
                form.setFieldsValue({
                    arrival_time: arrivalFlight.in_arrival_time
                        ? dayjs(arrivalFlight.in_arrival_time, "HH:mm:ss")
                        : null,
                    arrival_flight: arrivalFlight.in_flight_number || null,
                    arrival_location: arrivalFlight.in_arrival_airport || null,
                });
            }

            if (departureFlight) {
                form.setFieldsValue({
                    departure_flight_time: departureFlight.out_departure_time
                        ? dayjs(departureFlight.out_departure_time, "HH:mm:ss")
                        : null,
                    departure_flight: departureFlight.out_flight_number || null,
                    departure_location: departureFlight.out_departure_airport || null,
                });
            }
        } catch {
            message.error("Failed to fetch flights for autofill.");
        }
    };


    const handleCreate = async (values: any) => {
        try {
            const payload = {
                ...values,
                customer_id: values.customer,
                arrival_transportation: values.arrival_transportation === "Yes",
                departure_transportation: values.departure_transportation === "Yes",
                arrival_date:
                    values.arrival_transportation === "Yes" && values.arrival_date
                        ? values.arrival_date.format("YYYY-MM-DD")
                        : null,
                arrival_time:
                    values.arrival_transportation === "Yes" && values.arrival_time
                        ? values.arrival_time.format("HH:mm:ss")
                        : null,
                arrival_trans_pax:
                    values.arrival_transportation === "Yes" ? values.arrival_trans_pax : null,
                arrival_location:
                    values.arrival_transportation === "Yes" ? values.arrival_location : null,
                departure_date:
                    values.departure_transportation === "Yes" && values.departure_date
                        ? values.departure_date.format("YYYY-MM-DD")
                        : null,
                departure_time:
                    values.departure_transportation === "Yes" && values.departure_time
                        ? values.departure_time.format("HH:mm:ss")
                        : null,
                departure_trans_pax:
                    values.departure_transportation === "Yes" ? values.departure_trans_pax : null,
                departure_location:
                    values.departure_transportation === "Yes" ? values.departure_location : null,
                departure_flight_time:
                    values.departure_transportation === "Yes" && values.departure_flight_time
                        ? values.departure_flight_time.format("HH:mm:ss")
                        : null,
            };
            await api.post("/cc/transportation/", payload);
            message.success("Transportation entry created");
            setModalOpen(false);
            form.resetFields();
            fetchTransportations();
        } catch {
            message.error("Failed to create transportation entry");
        }
    };

    const handleExcelUpload = async (options: any) => {
        const {file, onSuccess, onError} = options;
        const formData = new FormData();
        formData.append("file", file);

        try {
            const response = await api.post("/cc/transportation-upload-excel/", formData, {
                headers: {"Content-Type": "multipart/form-data"},
            });

            message.success(response?.data?.message || "Transportation uploaded successfully");
            fetchTransportations(); // refresh table
            onSuccess?.("ok");
        } catch (error: any) {
            const msg = error?.response?.data?.error || "Upload failed.";
            const details = error?.response?.data?.details || [];

            message.error(msg);

            if (details.length > 0) {
                Modal.error({
                    title: "Upload Failed Details",
                    content: (
                        <div>
                            {details.map((d: string, idx: number) => (
                                <p key={idx} style={{marginBottom: 4}}>{d}</p>
                            ))}
                        </div>
                    ),
                    width: 600,
                    onOk: () => fetchTransportations(),
                });
            }

            onError?.(error);
        }
    };

    const uploadProps = {
        name: "file",
        accept: ".xlsx, .xls",
        maxCount: 1,
        customRequest: handleExcelUpload,
        showUploadList: false,
    };

    const renderBooleanIcon = (value: boolean) => {
        return value ? (
            <CheckCircleTwoTone twoToneColor="#52c41a"/>
        ) : (
            <CloseCircleTwoTone twoToneColor="#ff4d4f"/>
        );
    };

    const columns = [
        {
            title: "Customer",
            dataIndex: ["customer", "first_name"],
            render: (_: any, rec: any) => `${rec.customer.first_name} ${rec.customer.last_name}`,
        },
        {
            title: "Arrival Transport",
            dataIndex: "arrival_transportation",
            render: renderBooleanIcon,
        },
        {
            title: "Arrival Location",
            dataIndex: "arrival_location",
            render: (val: string, rec: any) =>
                rec.arrival_location ? val : "n/a",
        },
        {
            title: "Arrival Date",
            dataIndex: "arrival_date",
            render: (val: string, rec: any) =>
                rec.arrival_transportation ? val : "n/a",
        },
        {
            title: "Arrival Time",
            dataIndex: "arrival_time",
            render: (val: string, rec: any) =>
                rec.arrival_transportation && val ? val.slice(0, 5) : "n/a",
        },
        {
            title: "Departure Transport",
            dataIndex: "departure_transportation",
            render: renderBooleanIcon,
        },
        {
            title: "Departure Location",
            dataIndex: "departure_location",
            render: (val: string, rec: any) =>
                rec.departure_location ? val : "n/a",
        },
        {
            title: "Departure Date",
            dataIndex: "departure_date",
            render: (val: string, rec: any) =>
                rec.departure_transportation ? val : "n/a",
        },
        {
            title: "Departure Time",
            dataIndex: "departure_time",
            render: (val: string, rec: any) =>
                rec.departure_transportation && val ? val.slice(0, 5) : "n/a",
        },
    ];

    return (
        <Card
            className="rounded-2xl shadow-sm"
            title={
                <Row justify="space-between" align="middle" gutter={8}>
                    <Col>
                        <Title level={3} style={{margin: 0}}>
                            Casino Club Transportation
                        </Title>
                    </Col>
                    <Col flex="auto"/>
                    <Col>
                        <Upload {...uploadProps}>
                            <Button icon={<UploadOutlined/>}
                                    style={{
                                        backgroundColor: "#52c41a",
                                        color: "white",
                                        borderColor: "#52c41a",
                                        marginRight: 8
                                    }}>
                                Upload Excel
                            </Button>
                        </Upload>
                    </Col>
                    <Col>
                        <Button
                            type="primary"
                            icon={<PlusOutlined/>}
                            onClick={() => setModalOpen(true)}
                            className="rounded-md"
                        >
                            New Reservation
                        </Button>
                    </Col>
                </Row>
            }
        >
            <Row gutter={[16, 16]} style={{marginBottom: 16}} wrap>
                <Col xs={24} sm={12} md={8} lg={6}>
                    <Input
                        placeholder="Search by Name or MMID"
                        prefix={<SearchOutlined/>}
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        allowClear
                    />
                </Col>
                <Col xs={24} sm={12} md={8} lg={6}>
                    <RangePicker
                        onChange={(dates) => setArrivalDateRange(dates)}
                        style={{width: "100%"}}
                        placeholder={["Arrival Start", "Arrival End"]}
                    />
                </Col>
                <Col xs={24} sm={12} md={8} lg={6}>
                    <RangePicker
                        onChange={(dates) => setDepartureDateRange(dates)}
                        style={{width: "100%"}}
                        placeholder={["Departure Start", "Departure End"]}
                    />
                </Col>
                <Col xs={24} sm={12} md={6} lg={3}>
                    <Select
                        placeholder="Arrival Location"
                        style={{width: "100%"}}
                        allowClear
                        onChange={setArrivalLocation}
                    >
                        <Option value="ICN-T1">ICN-T1</Option>
                        <Option value="ICN-T2">ICN-T2</Option>
                        <Option value="GMP">GMP</Option>
                        <Option value="GMP-Private">GMP-Private</Option>
                    </Select>
                </Col>
                <Col xs={24} sm={12} md={6} lg={3}>
                    <Select
                        placeholder="Departure Location"
                        style={{width: "100%"}}
                        allowClear
                        onChange={setDepartureLocation}
                    >
                        <Option value="ICN-T1">ICN-T1</Option>
                        <Option value="ICN-T2">ICN-T2</Option>
                        <Option value="GMP">GMP</Option>
                        <Option value="GMP-Private">GMP-Private</Option>
                    </Select>
                </Col>
            </Row>

            <Table
                rowKey="id"
                loading={loading}
                dataSource={transportations}
                columns={columns}
                pagination={{
                    current: pagination.current,
                    pageSize: pagination.pageSize,
                    total,
                    showSizeChanger: true,
                    onChange: (page, pageSize) => {
                        setPagination({current: page, pageSize});
                    },
                }}
                scroll={{x: "max-content"}}
                onRow={(record) => {
                    const today = dayjs();

                    const hasArrival = !!record.arrival_date;
                    const hasDeparture = !!record.departure_date;

                    const arrivalIsPast = hasArrival && dayjs(record.arrival_date).isBefore(today, "day");
                    const departureIsPast = hasDeparture && dayjs(record.departure_date).isBefore(today, "day");

                    const shouldGreyOut =
                        (hasArrival && hasDeparture && departureIsPast) ||
                        (hasArrival && !hasDeparture && arrivalIsPast) ||
                        (!hasArrival && hasDeparture && departureIsPast);

                    return {
                        onClick: () => navigate(`/cc/transportation/${record.id}`),
                        style: {
                            cursor: "pointer",
                            transition: "background-color 0.3s ease",
                            backgroundColor: shouldGreyOut ? "#f5f5f5" : "#ffffff",
                        },
                    };
                }}
                className="rounded-lg overflow-hidden"
            />

            <Modal
                title="New Transportation"
                open={modalOpen}
                onCancel={() => setModalOpen(false)}
                onOk={() => form.submit()}
                centered
                width={700}
                okButtonProps={{className: "rounded-md"}}
                cancelButtonProps={{className: "rounded-md"}}
                bodyStyle={{maxHeight: "70vh", overflowY: "auto"}}
            >
                <Form
                    layout="vertical"
                    form={form}
                    onFinish={handleCreate}
                    onValuesChange={(changed, all) => {
                        const relevantFields = ["customer", "arrival_date", "departure_date"];
                        if (Object.keys(changed).some((key) => relevantFields.includes(key))) {
                            autofillFromFlights();
                        }
                    }}
                >
                    <Form.Item name="customer" label="Customer Name" rules={[{required: true}]}>
                        <Select
                            showSearch
                            placeholder="Select customer"
                            optionFilterProp="children"
                            filterOption={(input, option) =>
                                String(option?.children).toLowerCase().includes(input.toLowerCase())
                            }
                        >
                            {customers.map((cust) => (
                                <Option key={cust.id} value={cust.id}>
                                    {cust.first_name} {cust.last_name} ({cust.mmid})
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Divider orientation="left">Arrival</Divider>

                    <Form.Item name="arrival_transportation" label="Arrival Transportation">
                        <Select>
                            <Option value="Yes">Yes</Option>
                            <Option value="No">No</Option>
                        </Select>
                    </Form.Item>

                    {arrivalTransportation === "Yes" && (
                        <>
                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item name="arrival_trans_pax" label="Arrival Pax">
                                        <Select>
                                            {[1, 2, 3, 4, 5].map((n) => (
                                                <Option key={n} value={n}>
                                                    {n}
                                                </Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item name="arrival_date" label="Arrival Date">
                                        <DatePicker
                                            style={{width: "100%"}}
                                            disabledDate={(current) => current && current < dayjs().startOf("day")}
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Form.Item name="arrival_location" label="Arrival Location">
                                <Select>
                                    <Option value="ICN-T1">ICN-T1</Option>
                                    <Option value="ICN-T2">ICN-T2</Option>
                                    <Option value="GMP">GMP</Option>
                                    <Option value="GMP-Private">GMP-Private</Option>
                                </Select>
                            </Form.Item>

                            <Form.Item name="arrival_flight" label="Arrival Flight Number"><Input/></Form.Item>
                            <Form.Item name="arrival_time" label="Arrival Time"><TimePicker format="HH:mm"
                                                                                            style={{width: "100%"}}
                                                                                            allowClear/></Form.Item>
                            <Form.Item name="arrival_notes" label="Arrival Notes"><Input.TextArea rows={2}/></Form.Item>
                        </>
                    )}

                    <Divider orientation="left">Departure</Divider>

                    <Form.Item name="departure_transportation" label="Departure Transportation">
                        <Select>
                            <Option value="Yes">Yes</Option>
                            <Option value="No">No</Option>
                        </Select>
                    </Form.Item>

                    {departureTransportation === "Yes" && (
                        <>
                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item name="departure_trans_pax" label="Departure Pax">
                                        <Select>
                                            {[1, 2, 3, 4, 5].map((n) => (
                                                <Option key={n} value={n}>
                                                    {n}
                                                </Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item name="departure_date" label="Departure Date">
                                        <DatePicker
                                            style={{width: "100%"}}
                                            disabledDate={(current) => current && current < dayjs().startOf("day")}
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Form.Item name="departure_location" label="Departure Location">
                                <Select>
                                    <Option value="ICN-T1">ICN-T1</Option>
                                    <Option value="ICN-T2">ICN-T2</Option>
                                    <Option value="GMP">GMP</Option>
                                    <Option value="GMP-Private">GMP-Private</Option>
                                </Select>
                            </Form.Item>

                            <Form.Item name="departure_flight" label="Departure Flight Number"><Input/></Form.Item>
                            <Form.Item name="departure_time" label="VIP Lobby Departure Time"><TimePicker format="HH:mm"
                                                                                                          style={{width: "100%"}}
                                                                                                          allowClear/></Form.Item>
                            <Form.Item name="departure_flight_time" label="Flight Departure Time">
                                <TimePicker format="HH:mm" style={{width: "100%"}} allowClear/>
                            </Form.Item>
                            <Form.Item name="departure_notes" label="Departure Notes"><Input.TextArea
                                rows={2}/></Form.Item>
                        </>
                    )}
                </Form>
            </Modal>
        </Card>
    );
};

export default TransportationPage;
